import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {
  appSyncApi,
  SubscribeToNotificationsApiArg,
  useSubscribeToNotificationsQuery,
} from '@dms/api/appsync';
import type {TestSmtpEmailCredentialsRequestBody} from '@dms/api/core';
import {notificationTypes} from '@dms/shared';

import {useOnMount} from 'shared/web';

import {SMTP_CREDENTIALS_TEST_TIMEOUT_MS} from '../constants/SMTP_CREDENTIALS_TEST_TIMEOUT_MS';
import {smtpCredentialsTestStatus} from '../constants/smtpCredentialsTestStatus';
import type {SmtpCredentialsTestRun} from '../types/SmtpCredentialsTestRun';

export function useSmtpCredentialsTest() {
  const [run, setRun] = useState<SmtpCredentialsTestRun | null>(null);
  const [testedValues, setTestedValues] = useState<TestSmtpEmailCredentialsRequestBody | null>(
    null
  );
  const activeRunIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingSnapshotsRef = useRef<Map<string, SmtpCredentialsTestRun> | null>(null);
  const lastNotificationRef = useRef<SubscribeToNotificationsApiArg | null>(null);

  useOnMount(undefined, () => clearTimeout(timeoutRef.current));

  const prepare = () => {
    clearTimeout(timeoutRef.current);
    activeRunIdRef.current = null;
    pendingSnapshotsRef.current = new Map();
    setRun(null);
    setTestedValues(null);
  };

  const cancelPending = () => {
    pendingSnapshotsRef.current = null;
  };

  const start = (testRunId: string, values: TestSmtpEmailCredentialsRequestBody) => {
    clearTimeout(timeoutRef.current);
    setTestedValues(values);
    const snapshot = pendingSnapshotsRef.current?.get(testRunId);
    cancelPending();
    if (isNotNil(snapshot) && snapshot.overallStatus !== smtpCredentialsTestStatus.inProgress) {
      activeRunIdRef.current = null;
      setRun(snapshot);
      return;
    }
    activeRunIdRef.current = testRunId;
    setRun(
      snapshot ?? {
        testRunId,
        overallStatus: smtpCredentialsTestStatus.inProgress,
        tests: [],
        isTimedOut: false,
      }
    );
    timeoutRef.current = setTimeout(() => {
      setRun((current) => {
        if (
          current?.testRunId !== testRunId ||
          current.overallStatus !== smtpCredentialsTestStatus.inProgress
        ) {
          return current;
        }
        return {...current, isTimedOut: true};
      });
    }, SMTP_CREDENTIALS_TEST_TIMEOUT_MS);
  };

  const acceptNotification = (notification: SubscribeToNotificationsApiArg) => {
    if (
      notification.variant !== 'background' ||
      notification.type !== notificationTypes.EMAIL_CREDENTIALS_SMTP_TEST_STATE_CHANGED
    ) {
      return;
    }

    const payload = notification.payload;
    const pendingSnapshots = pendingSnapshotsRef.current;
    if (isNotNil(pendingSnapshots)) {
      const previous = pendingSnapshots.get(payload.testRunId);
      // Keep the first terminal snapshot even when notification fetches finish out of order.
      if (!isNotNil(previous) || previous.overallStatus === smtpCredentialsTestStatus.inProgress) {
        pendingSnapshots.set(payload.testRunId, {...payload, isTimedOut: false});
      }
      return;
    }
    if (payload.testRunId !== activeRunIdRef.current) {
      return;
    }

    setRun((current) => {
      if (
        current?.testRunId === payload.testRunId &&
        current.isTimedOut &&
        payload.overallStatus === smtpCredentialsTestStatus.inProgress
      ) {
        return current;
      }
      return {...payload, isTimedOut: false};
    });
    if (payload.overallStatus !== smtpCredentialsTestStatus.inProgress) {
      activeRunIdRef.current = null;
      clearTimeout(timeoutRef.current);
    }
  };

  const {data: notifications} = appSyncApi.endpoints.subscribeToNotifications.useQueryState();
  useSubscribeToNotificationsQuery((notification) => {
    // The subscription callback forwards only the last item of a React-batched update.
    // Read unseen buffer entries too, so an earlier terminal snapshot cannot be lost or replayed.
    if (!isNotNil(notifications)) {
      acceptNotification(notification);
      lastNotificationRef.current = notification;
      return;
    }
    const lastIndex = isNotNil(lastNotificationRef.current)
      ? notifications.indexOf(lastNotificationRef.current)
      : -1;
    for (const snapshot of notifications.slice(lastIndex + 1)) {
      acceptNotification(snapshot);
    }
    lastNotificationRef.current = notifications.at(-1) ?? lastNotificationRef.current;
  });

  const isInProgress =
    run?.overallStatus === smtpCredentialsTestStatus.inProgress && !run.isTimedOut;

  return {
    run,
    testedValues,
    isInProgress,
    hasCompletedRun: isNotNil(run) && !isInProgress,
    prepare,
    cancelPending,
    start,
  };
}
